import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { equipment as equipmentApi } from '@/utils/api'
import { useEquipmentAction, useEquipmentMeta } from '@/hooks/useEquipment'
import type { Equipment, EquipmentLocationType, MoveTarget } from '@/types/equipment'
import { LOCATION_CONFIG, LOCATION_KEYS } from './equipmentUtils'

interface Props {
  item: Equipment | null
  onClose: () => void
}

export default function MoveDialog({ item, onClose }: Props) {
  const { data: meta } = useEquipmentMeta()
  const [type, setType] = useState<EquipmentLocationType>('projekt')
  const [targetId, setTargetId] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (item) { setType(item.location_type === 'projekt' ? 'lager' : 'projekt'); setTargetId(''); setNote('') }
  }, [item])

  const options = useMemo(() => {
    if (!meta) return []
    if (type === 'projekt') return meta.projects.map((p) => ({ value: String(p.id), label: p.name }))
    if (type === 'mitarbeiter') return meta.users.map((u) => ({ value: String(u.id), label: u.name || u.username }))
    return meta.places
      .filter((p) => p.type === type)
      .map((p) => ({ value: String(p.id), label: p.license_plate ? `${p.name} (${p.license_plate})` : p.name }))
  }, [meta, type])

  // Nur ein Lager → direkt vorwählen
  useEffect(() => {
    if (options.length === 1 && (type === 'lager' || type === 'fahrzeug')) setTargetId(options[0].value)
  }, [options, type])

  const move = useEquipmentAction(
    ({ id, target }: { id: number; target: MoveTarget }) => equipmentApi.move(id, target),
    (d: Equipment) => `Umgebucht nach ${d.location_label}`,
  )

  const submit = () => {
    if (!item || !targetId) return
    const target: MoveTarget = { locationType: type, note: note || undefined }
    if (type === 'projekt') target.projectId = Number(targetId)
    else if (type === 'mitarbeiter') target.userId = Number(targetId)
    else target.placeId = Number(targetId)
    move.mutate({ id: item.id, target }, { onSuccess: onClose })
  }

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Umbuchen: {item?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Aktuell: <span className="font-medium text-foreground">{item?.location_label || '–'}</span></p>
          <div className="grid grid-cols-4 gap-2">
            {LOCATION_KEYS.map((key) => {
              const cfg = LOCATION_CONFIG[key]
              const Icon = cfg.icon
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setType(key); setTargetId('') }}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-lg border p-2 text-xs transition-colors',
                    type === key ? 'border-primary-500 bg-primary-50 text-primary-700' : 'hover:bg-gray-50',
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {cfg.label}
                </button>
              )
            })}
          </div>
          <div className="space-y-1.5">
            <Label>Ziel</Label>
            <Combobox
              options={options}
              value={targetId}
              onChange={setTargetId}
              placeholder={options.length ? 'Auswählen…' : type === 'fahrzeug' ? 'Keine Fahrzeuge angelegt (Tab „Orte")' : 'Keine Einträge'}
              searchPlaceholder="Suchen…"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Notiz (optional)</Label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-white px-3 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={!targetId || move.isPending}>
            {move.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Umbuchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
